/**
 * White Label Admin Component
 * Allows platform admins to configure white-label branding for the platform
 * Adapted from term-sheets-dashboard with dark mode support and API integration
 */

import React, { useState, useEffect, useRef } from 'react';
import { Upload, Save, Eye, EyeOff, Palette, Image as ImageIcon, Settings, Globe, Mail, RefreshCw, RotateCcw, ChevronDown, Plus, Trash2, Copy, Wand2, Sparkles, Check, FileText, Building2, Phone, MapPin, Download } from 'lucide-react';
import api from '../lib/api';
import Swal from 'sweetalert2';
import { useWhiteLabel } from '../context/WhiteLabelContext';
import { generateSamplePDF } from '../utils/WhiteLabelPDFGenerator';

// Color extraction and suggestion utilities
const ColorUtils = {
  // Convert hex to RGB
  hexToRgb: (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  },

  // Convert RGB to hex
  rgbToHex: (r, g, b) => {
    return '#' + [r, g, b].map(x => {
      const hex = Math.round(Math.max(0, Math.min(255, x))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  },

  // Convert RGB to HSL
  rgbToHsl: (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
  },

  // Convert HSL to RGB
  hslToRgb: (h, s, l) => {
    h /= 360; s /= 100; l /= 100;
    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
  },

  // Generate darker shade
  darken: (hex, percent) => {
    const rgb = ColorUtils.hexToRgb(hex);
    if (!rgb) return hex;
    const hsl = ColorUtils.rgbToHsl(rgb.r, rgb.g, rgb.b);
    hsl.l = Math.max(0, hsl.l - percent);
    const newRgb = ColorUtils.hslToRgb(hsl.h, hsl.s, hsl.l);
    return ColorUtils.rgbToHex(newRgb.r, newRgb.g, newRgb.b);
  },

  // Generate lighter shade
  lighten: (hex, percent) => {
    const rgb = ColorUtils.hexToRgb(hex);
    if (!rgb) return hex;
    const hsl = ColorUtils.rgbToHsl(rgb.r, rgb.g, rgb.b);
    hsl.l = Math.min(100, hsl.l + percent);
    const newRgb = ColorUtils.hslToRgb(hsl.h, hsl.s, hsl.l);
    return ColorUtils.rgbToHex(newRgb.r, newRgb.g, newRgb.b);
  },

  // Adjust saturation
  saturate: (hex, percent) => {
    const rgb = ColorUtils.hexToRgb(hex);
    if (!rgb) return hex;
    const hsl = ColorUtils.rgbToHsl(rgb.r, rgb.g, rgb.b);
    hsl.s = Math.min(100, hsl.s + percent);
    const newRgb = ColorUtils.hslToRgb(hsl.h, hsl.s, hsl.l);
    return ColorUtils.rgbToHex(newRgb.r, newRgb.g, newRgb.b);
  },

  // Get color luminance for contrast
  getLuminance: (hex) => {
    const rgb = ColorUtils.hexToRgb(hex);
    if (!rgb) return 0;
    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(v => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  },

  // Check if color is dark
  isDark: (hex) => ColorUtils.getLuminance(hex) < 0.5,

  // Extract dominant colors from image
  extractColorsFromImage: (imageData, colorCount = 5) => {
    const pixels = [];
    const data = imageData.data;

    // Sample pixels (skip transparent and near-white/black)
    for (let i = 0; i < data.length; i += 16) { // Sample every 4th pixel for performance
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      // Skip transparent pixels
      if (a < 128) continue;

      // Skip near-white and near-black
      const brightness = (r + g + b) / 3;
      if (brightness > 240 || brightness < 15) continue;

      // Skip very desaturated colors (grays)
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const saturation = max === 0 ? 0 : (max - min) / max;
      if (saturation < 0.15 && brightness > 50 && brightness < 200) continue;

      pixels.push({ r, g, b });
    }

    if (pixels.length === 0) return [];

    // Simple k-means clustering for color extraction
    const clusters = ColorUtils.kMeansClustering(pixels, colorCount);

    // Sort by cluster size (most dominant first)
    clusters.sort((a, b) => b.count - a.count);

    return clusters.map(c => ({
      hex: ColorUtils.rgbToHex(c.r, c.g, c.b),
      count: c.count,
      rgb: { r: c.r, g: c.g, b: c.b }
    }));
  },

  // K-means clustering for color grouping
  kMeansClustering: (pixels, k) => {
    if (pixels.length < k) k = pixels.length;

    // Initialize centroids randomly
    let centroids = [];
    const used = new Set();
    while (centroids.length < k) {
      const idx = Math.floor(Math.random() * pixels.length);
      if (!used.has(idx)) {
        used.add(idx);
        centroids.push({ ...pixels[idx], count: 0 });
      }
    }

    // Iterate
    for (let iter = 0; iter < 10; iter++) {
      // Reset counts
      centroids.forEach(c => { c.count = 0; c.sumR = 0; c.sumG = 0; c.sumB = 0; });

      // Assign pixels to nearest centroid
      pixels.forEach(pixel => {
        let minDist = Infinity;
        let nearest = 0;
        centroids.forEach((c, i) => {
          const dist = Math.pow(pixel.r - c.r, 2) + Math.pow(pixel.g - c.g, 2) + Math.pow(pixel.b - c.b, 2);
          if (dist < minDist) {
            minDist = dist;
            nearest = i;
          }
        });
        centroids[nearest].count++;
        centroids[nearest].sumR = (centroids[nearest].sumR || 0) + pixel.r;
        centroids[nearest].sumG = (centroids[nearest].sumG || 0) + pixel.g;
        centroids[nearest].sumB = (centroids[nearest].sumB || 0) + pixel.b;
      });

      // Update centroids
      centroids.forEach(c => {
        if (c.count > 0) {
          c.r = Math.round(c.sumR / c.count);
          c.g = Math.round(c.sumG / c.count);
          c.b = Math.round(c.sumB / c.count);
        }
      });
    }

    return centroids.filter(c => c.count > 0);
  },

  // Generate color scheme from primary color
  generateScheme: (primaryHex) => {
    const rgb = ColorUtils.hexToRgb(primaryHex);
    if (!rgb) return null;

    const hsl = ColorUtils.rgbToHsl(rgb.r, rgb.g, rgb.b);

    // Secondary: Darker shade of primary
    const secondary = ColorUtils.darken(primaryHex, 15);

    // Accent: Lighter and more saturated version
    const accent = ColorUtils.lighten(ColorUtils.saturate(primaryHex, 10), 10);

    return { primary: primaryHex, secondary, accent };
  }
};

// Default configuration values
const DEFAULT_CONFIG = {
  companyName: 'HaliTrade',
  tagline: 'Trade Finance Platform',
  domain: 'halisoft.biz',
  primaryColor: '#DC2626',
  secondaryColor: '#B91C1C',
  accentColor: '#EF4444',
  logoUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  emailLogoUrl: null,
  loginBgImage: null,
  poweredBy: 'powered by HaliSoft',
  supportEmail: 'support@halisoft.biz',
  useCustomEmails: true,
  // Letterhead / PDF configuration
  letterheadLogoUrl: null,
  letterheadCompanyName: '',
  letterheadTagline: '',
  letterheadRegistrationNumber: '',
  letterheadSignatoryName: '',
  letterheadSignatoryTitle: '',
  letterheadAddress: '',
  letterheadCity: '',
  letterheadCountry: '',
  letterheadPhone: '',
  letterheadEmail: '',
  letterheadWebsite: '',
  letterheadHeaderGradientStart: '#0ea5e9',
  letterheadHeaderGradientEnd: '#0d9488',
  letterheadFooterColor: '#374151'
};

// Preset profiles for quick selection
const PRESET_PROFILES = {
  halitrade: {
    name: 'HaliTrade',
    companyName: 'HaliTrade',
    tagline: 'Trade Finance Platform',
    domain: 'halitrade.com',
    primaryColor: '#DC2626',
    secondaryColor: '#B91C1C',
    accentColor: '#EF4444',
    poweredBy: 'powered by HaliSoft',
    supportEmail: 'support@halisoft.biz'
  },
  incomlend: {
    name: 'Incomlend',
    companyName: 'Incomlend',
    tagline: 'Invoice Finance Solutions',
    domain: 'incomlend.com',
    primaryColor: '#0066CC',
    secondaryColor: '#004499',
    accentColor: '#3399FF',
    poweredBy: '',
    supportEmail: 'support@incomlend.com'
  },
  tradeflow: {
    name: 'TradeFlow',
    companyName: 'TradeFlow',
    tagline: 'Global Trade Finance',
    domain: 'tradeflow.io',
    primaryColor: '#059669',
    secondaryColor: '#047857',
    accentColor: '#10B981',
    poweredBy: 'powered by HaliSoft',
    supportEmail: 'support@tradeflow.io'
  }
};

const WhiteLabelAdmin = ({ darkMode = false, embedded = false, initialTenantCode = null, onSaveSuccess = null }) => {
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG });

  const [preview, setPreview] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Profile management state
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const profilesRef = useRef([]); // Ref to access latest profiles in callbacks

  // Color extraction state
  const [extractedColors, setExtractedColors] = useState([]);
  const [suggestedScheme, setSuggestedScheme] = useState(null);
  const [extractingColors, setExtractingColors] = useState(false);
  const canvasRef = useRef(null);

  // Website extraction state
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [extractingFromWebsite, setExtractingFromWebsite] = useState(false);

  // Get white-label context to update app-wide branding after save
  const { switchProfile, currentProfileCode } = useWhiteLabel();

  // Fetch existing configuration and profiles on mount
  useEffect(() => {
    if (initialTenantCode) {
      // Load specific tenant configuration
      fetchTenantConfig(initialTenantCode);
    } else {
      fetchConfig();
    }
    // Always fetch profiles so we have the correct format with IDs
    fetchProfiles();
  }, [initialTenantCode]);

  // Fetch specific tenant configuration by code
  const fetchTenantConfig = async (tenantCode) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/api/white-label/tenant/${tenantCode}`);
      if (data) {
        setConfig({
          companyName: data.company_name || 'HaliTrade',
          tagline: data.tagline || 'Trade Finance Platform',
          domain: data.custom_domain || data.subdomain || 'halisoft.biz',
          primaryColor: data.primary_color || '#DC2626',
          secondaryColor: data.secondary_color || '#B91C1C',
          accentColor: data.accent_color || '#EF4444',
          logoUrl: data.logo_url || null,
          logoDarkUrl: data.logo_dark_url || null,
          faviconUrl: data.favicon_url || null,
          emailLogoUrl: data.email_logo_url || null,
          loginBgImage: data.login_bg_image_url || null,
          poweredBy: data.powered_by || '',
          supportEmail: data.support_email || 'support@halisoft.biz',
          useCustomEmails: data.use_custom_emails !== false,
          tenantId: data.id,
          tenantCode: data.tenant_code,
          // Letterhead / PDF branding fields
          letterheadLogoUrl: data.letterhead_logo_url || null,
          letterheadCompanyName: data.letterhead_company_name || '',
          letterheadTagline: data.letterhead_tagline || '',
          letterheadRegistrationNumber: data.letterhead_registration_number || '',
          letterheadSignatoryName: data.letterhead_signatory_name || '',
          letterheadSignatoryTitle: data.letterhead_signatory_title || '',
          letterheadAddress: data.letterhead_address || '',
          letterheadCity: data.letterhead_city || '',
          letterheadCountry: data.letterhead_country || '',
          letterheadPhone: data.letterhead_phone || '',
          letterheadEmail: data.letterhead_email || '',
          letterheadWebsite: data.letterhead_website || '',
          letterheadHeaderGradientStart: data.letterhead_header_gradient_start || '#0ea5e9',
          letterheadHeaderGradientEnd: data.letterhead_header_gradient_end || '#0d9488',
          letterheadFooterColor: data.letterhead_footer_color || '#374151'
        });
        setSelectedProfile({
          id: data.id,
          name: data.company_name || 'Profile',
          tenantCode: data.tenant_code
        });
      }
    } catch (error) {
      console.log('Error fetching tenant config:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load profile configuration',
        background: darkMode ? '#1F2937' : '#fff',
        color: darkMode ? '#F9FAFB' : '#111827'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/white-label/current');
      if (data) {
        setConfig({
          companyName: data.company_name || 'HaliTrade',
          tagline: data.tagline || 'Trade Finance Platform',
          domain: data.custom_domain || data.subdomain || 'halisoft.biz',
          primaryColor: data.primary_color || '#DC2626',
          secondaryColor: data.secondary_color || '#B91C1C',
          accentColor: data.accent_color || '#EF4444',
          logoUrl: data.logo_url || null,
          logoDarkUrl: data.logo_dark_url || null,
          faviconUrl: data.favicon_url || null,
          emailLogoUrl: data.email_logo_url || null,
          loginBgImage: data.login_bg_image_url || null,
          poweredBy: data.powered_by || '',
          supportEmail: data.support_email || 'support@halisoft.biz',
          useCustomEmails: data.use_custom_emails !== false,
          tenantId: data.id,
          // Letterhead / PDF branding fields
          letterheadLogoUrl: data.letterhead_logo_url || null,
          letterheadCompanyName: data.letterhead_company_name || '',
          letterheadTagline: data.letterhead_tagline || '',
          letterheadRegistrationNumber: data.letterhead_registration_number || '',
          letterheadSignatoryName: data.letterhead_signatory_name || '',
          letterheadSignatoryTitle: data.letterhead_signatory_title || '',
          letterheadAddress: data.letterhead_address || '',
          letterheadCity: data.letterhead_city || '',
          letterheadCountry: data.letterhead_country || '',
          letterheadPhone: data.letterhead_phone || '',
          letterheadEmail: data.letterhead_email || '',
          letterheadWebsite: data.letterhead_website || '',
          letterheadHeaderGradientStart: data.letterhead_header_gradient_start || '#0ea5e9',
          letterheadHeaderGradientEnd: data.letterhead_header_gradient_end || '#0d9488',
          letterheadFooterColor: data.letterhead_footer_color || '#374151'
        });
        // Set the selected profile
        setSelectedProfile({
          id: data.id,
          name: data.company_name || 'HaliTrade',
          tenantCode: data.tenant_code
        });
      } else {
        // No config found, use HaliTrade preset as default
        setSelectedProfile(PRESET_PROFILES.halitrade);
      }
    } catch (error) {
      console.log('No white label config found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all available profiles (tenants)
  const fetchProfiles = async () => {
    try {
      setLoadingProfiles(true);
      // console.log('[WhiteLabelAdmin] fetchProfiles - fetching from API...');
      const { data } = await api.get('/api/white-label/tenants');
      // console.log('[WhiteLabelAdmin] fetchProfiles - API response:', data);
      if (data && Array.isArray(data)) {
        const mappedProfiles = data.map(tenant => ({
          id: tenant.id,
          tenantCode: tenant.tenant_code,
          name: tenant.company_name,
          companyName: tenant.company_name,
          tagline: tenant.tagline,
          domain: tenant.custom_domain || tenant.subdomain,
          primaryColor: tenant.primary_color,
          secondaryColor: tenant.secondary_color,
          accentColor: tenant.accent_color,
          poweredBy: tenant.powered_by,
          supportEmail: tenant.support_email,
          logoUrl: tenant.logo_url,
          logoDarkUrl: tenant.logo_dark_url,
          faviconUrl: tenant.favicon_url,
          isActive: tenant.is_active,
          isDefault: tenant.is_default
        }));
        // console.log('[WhiteLabelAdmin] fetchProfiles - mapped profiles:', mappedProfiles);
        setProfiles(mappedProfiles);
        profilesRef.current = mappedProfiles; // Update ref immediately
      }
    } catch (error) {
      console.log('Failed to fetch profiles:', error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  // Load a profile into the editor
  const loadProfile = (profile) => {
    // console.log('[WhiteLabelAdmin] loadProfile called with:', profile.name);

    // Get the company name from profile
    const companyName = profile.companyName || profile.name || profile.company_name;

    // Get the tenant ID - could be id, tenantId, or tenant_id depending on source
    let profileId = profile.id || profile.tenantId || profile.tenant_id;
    let tenantCode = profile.tenantCode || profile.tenant_code;

    // If no ID, try to find matching profile from DB by name
    // Use profilesRef.current to get the latest profiles (handles async timing)
    const currentProfiles = profilesRef.current.length > 0 ? profilesRef.current : profiles;
    // console.log('[WhiteLabelAdmin] Looking for match in', currentProfiles.length, 'profiles');
    if (!profileId && currentProfiles.length > 0) {
      const matchingDbProfile = currentProfiles.find(p =>
        p.name?.toLowerCase() === companyName?.toLowerCase() ||
        p.companyName?.toLowerCase() === companyName?.toLowerCase()
      );
      if (matchingDbProfile) {
        // console.log('[WhiteLabelAdmin] Found matching DB profile:', matchingDbProfile?.name);
        profileId = matchingDbProfile.id;
        tenantCode = matchingDbProfile.tenantCode || tenantCode;
      }
    }

    // console.log('[WhiteLabelAdmin] Using profileId:', profileId, 'tenantCode:', tenantCode);

    setConfig({
      ...config,
      companyName: companyName,
      tagline: profile.tagline || '',
      domain: profile.domain || profile.custom_domain || '',
      primaryColor: profile.primaryColor || profile.primary_color || DEFAULT_CONFIG.primaryColor,
      secondaryColor: profile.secondaryColor || profile.secondary_color || DEFAULT_CONFIG.secondaryColor,
      accentColor: profile.accentColor || profile.accent_color || DEFAULT_CONFIG.accentColor,
      logoUrl: profile.logoUrl || profile.logo_url || null,
      logoDarkUrl: profile.logoDarkUrl || profile.logo_dark_url || null,
      faviconUrl: profile.faviconUrl || profile.favicon_url || null,
      poweredBy: profile.poweredBy || profile.powered_by || '',
      supportEmail: profile.supportEmail || profile.support_email || '',
      tenantId: profileId,
      tenantCode: tenantCode || null,
      // Auto-populate letterhead fields from profile if not already set
      letterheadCompanyName: profile.letterheadCompanyName || profile.letterhead_company_name || companyName,
      letterheadTagline: profile.letterheadTagline || profile.letterhead_tagline || profile.tagline || config.letterheadTagline,
      letterheadLogoUrl: profile.letterheadLogoUrl || profile.letterhead_logo_url || profile.logoUrl || config.letterheadLogoUrl,
      letterheadEmail: profile.letterheadEmail || profile.letterhead_email || profile.supportEmail || config.letterheadEmail,
      letterheadWebsite: profile.letterheadWebsite || profile.letterhead_website || profile.domain || config.letterheadWebsite,
      letterheadAddress: profile.letterheadAddress || profile.letterhead_address || config.letterheadAddress,
      letterheadCity: profile.letterheadCity || profile.letterhead_city || config.letterheadCity,
      letterheadCountry: profile.letterheadCountry || profile.letterhead_country || config.letterheadCountry,
      letterheadPhone: profile.letterheadPhone || profile.letterhead_phone || config.letterheadPhone,
      letterheadSignatoryName: profile.letterheadSignatoryName || profile.letterhead_signatory_name || config.letterheadSignatoryName,
      letterheadSignatoryTitle: profile.letterheadSignatoryTitle || profile.letterhead_signatory_title || config.letterheadSignatoryTitle,
      letterheadRegistrationNumber: profile.letterheadRegistrationNumber || profile.letterhead_registration_number || config.letterheadRegistrationNumber,
      letterheadHeaderGradientStart: profile.letterheadHeaderGradientStart || profile.letterhead_header_gradient_start || profile.primaryColor || config.letterheadHeaderGradientStart,
      letterheadHeaderGradientEnd: profile.letterheadHeaderGradientEnd || profile.letterhead_header_gradient_end || profile.secondaryColor || config.letterheadHeaderGradientEnd,
      letterheadFooterColor: profile.letterheadFooterColor || profile.letterhead_footer_color || config.letterheadFooterColor
    });
    setSelectedProfile(profile);
    setShowProfileDropdown(false);

    Swal.fire({
      icon: 'success',
      title: 'Profile Loaded',
      text: `Loaded "${profile.name}" profile settings`,
      timer: 1500,
      showConfirmButton: false,
      background: darkMode ? '#1F2937' : '#fff',
      color: darkMode ? '#F9FAFB' : '#111827'
    });
  };

  // Save current config as a new profile
  const saveAsNewProfile = async () => {
    const { value: profileName } = await Swal.fire({
      title: 'Save as New Profile',
      input: 'text',
      inputLabel: 'Profile Name',
      inputPlaceholder: 'e.g., My Company',
      inputValue: config.companyName,
      showCancelButton: true,
      confirmButtonColor: '#f97316',
      background: darkMode ? '#1F2937' : '#fff',
      color: darkMode ? '#F9FAFB' : '#111827',
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter a profile name';
        }
      }
    });

    if (profileName) {
      setSaving(true);
      try {
        const tenantCode = profileName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        const payload = {
          tenant_code: tenantCode,
          company_name: profileName,
          tagline: config.tagline,
          custom_domain: config.domain,
          primary_color: config.primaryColor,
          secondary_color: config.secondaryColor,
          accent_color: config.accentColor,
          logo_url: config.logoUrl,
          logo_dark_url: config.logoDarkUrl,
          favicon_url: config.faviconUrl,
          email_logo_url: config.emailLogoUrl,
          login_bg_image_url: config.loginBgImage,
          powered_by: config.poweredBy,
          support_email: config.supportEmail,
          use_custom_emails: config.useCustomEmails
        };

        const { data } = await api.post('/api/white-label/tenants', payload);

        // Refresh profiles list
        await fetchProfiles();

        // Update current config with new tenant ID
        setConfig({ ...config, tenantId: data.id });
        setSelectedProfile({ id: data.id, name: profileName, tenantCode });

        Swal.fire({
          icon: 'success',
          title: 'Profile Created',
          text: `"${profileName}" profile has been saved`,
          timer: 2000,
          showConfirmButton: false,
          background: darkMode ? '#1F2937' : '#fff',
          color: darkMode ? '#F9FAFB' : '#111827'
        });
      } catch (error) {
        console.error('Error creating profile:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.error || 'Failed to create profile',
          background: darkMode ? '#1F2937' : '#fff',
          color: darkMode ? '#F9FAFB' : '#111827'
        });
      } finally {
        setSaving(false);
      }
    }
  };

  // Delete a profile
  const deleteProfile = async (profile) => {
    if (profile.isDefault) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Delete',
        text: 'Default profile cannot be deleted',
        background: darkMode ? '#1F2937' : '#fff',
        color: darkMode ? '#F9FAFB' : '#111827'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Profile?',
      text: `Are you sure you want to delete "${profile.name}"? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it',
      background: darkMode ? '#1F2937' : '#fff',
      color: darkMode ? '#F9FAFB' : '#111827'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/white-label/tenants/${profile.id}`);
        await fetchProfiles();

        if (selectedProfile?.id === profile.id) {
          setSelectedProfile(null);
          setConfig({ ...DEFAULT_CONFIG });
        }

        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: `"${profile.name}" has been deleted`,
          timer: 1500,
          showConfirmButton: false,
          background: darkMode ? '#1F2937' : '#fff',
          color: darkMode ? '#F9FAFB' : '#111827'
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete profile',
          background: darkMode ? '#1F2937' : '#fff',
          color: darkMode ? '#F9FAFB' : '#111827'
        });
      }
    }
  };

  const handleColorChange = (field, value) => {
    setConfig({ ...config, [field]: value });
  };

  const handleFileUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setConfig({ ...config, [field]: reader.result });

        // Auto-extract colors when logo is uploaded
        if (field === 'logoUrl') {
          extractColorsFromLogo(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Drag & Drop handlers for file upload
  const [dragActive, setDragActive] = useState({
    logoUrl: false,
    logoDarkUrl: false,
    faviconUrl: false,
    emailLogoUrl: false,
    letterheadLogoUrl: false
  });

  const handleDrag = (field, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(prev => ({ ...prev, [field]: true }));
    } else if (e.type === 'dragleave') {
      setDragActive(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleDrop = (field, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [field]: false }));

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setConfig(prev => ({ ...prev, [field]: reader.result }));

          // Auto-extract colors when PRIMARY logo is dropped
          if (field === 'logoUrl') {
            extractColorsFromLogo(reader.result);
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Extract colors from uploaded logo image
  const extractColorsFromLogo = (imageDataUrl) => {
    setExtractingColors(true);
    setExtractedColors([]);
    setSuggestedScheme(null);

    const img = new window.Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        // Create canvas if not exists
        const canvas = canvasRef.current || document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Scale down large images for performance
        const maxSize = 200;
        const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // Extract dominant colors
        const colors = ColorUtils.extractColorsFromImage(imageData, 6);
        setExtractedColors(colors);

        // Auto-generate scheme from the most dominant color
        if (colors.length > 0) {
          const scheme = ColorUtils.generateScheme(colors[0].hex);
          setSuggestedScheme(scheme);
        }

        Swal.fire({
          icon: 'success',
          title: 'Colors Extracted',
          text: `Found ${colors.length} dominant colors from your logo`,
          timer: 2000,
          showConfirmButton: false,
          background: darkMode ? '#1F2937' : '#fff',
          color: darkMode ? '#F9FAFB' : '#111827'
        });
      } catch (err) {
        console.error('Error extracting colors:', err);
        Swal.fire({
          icon: 'error',
          title: 'Extraction Failed',
          text: 'Could not extract colors from this image',
          background: darkMode ? '#1F2937' : '#fff',
          color: darkMode ? '#F9FAFB' : '#111827'
        });
      } finally {
        setExtractingColors(false);
      }
    };

    img.onerror = () => {
      setExtractingColors(false);
      console.error('Failed to load image for color extraction');
    };

    img.src = imageDataUrl;
  };

  // Generate color scheme from primary color
  const generateSchemeFromPrimary = () => {
    const scheme = ColorUtils.generateScheme(config.primaryColor);
    if (scheme) {
      setSuggestedScheme(scheme);
      Swal.fire({
        icon: 'info',
        title: 'Scheme Generated',
        html: `Based on <strong>${config.primaryColor}</strong>, we suggest:<br/>
               Secondary: <strong>${scheme.secondary}</strong><br/>
               Accent: <strong>${scheme.accent}</strong>`,
        showConfirmButton: true,
        confirmButtonText: 'Apply Scheme',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonColor: config.primaryColor,
        background: darkMode ? '#1F2937' : '#fff',
        color: darkMode ? '#F9FAFB' : '#111827'
      }).then((result) => {
        if (result.isConfirmed) {
          applyColorScheme(scheme);
        }
      });
    }
  };

  // Apply a color scheme to the config
  const applyColorScheme = (scheme) => {
    setConfig({
      ...config,
      primaryColor: scheme.primary,
      secondaryColor: scheme.secondary,
      accentColor: scheme.accent
    });

    Swal.fire({
      icon: 'success',
      title: 'Colors Applied',
      text: 'Color scheme has been applied to your branding',
      timer: 1500,
      showConfirmButton: false,
      background: darkMode ? '#1F2937' : '#fff',
      color: darkMode ? '#F9FAFB' : '#111827'
    });
  };

  // Apply a single extracted color as primary and generate scheme
  const applyExtractedColor = (colorHex) => {
    const scheme = ColorUtils.generateScheme(colorHex);
    if (scheme) {
      setConfig({
        ...config,
        primaryColor: scheme.primary,
        secondaryColor: scheme.secondary,
        accentColor: scheme.accent
      });
      setSuggestedScheme(scheme);
    }
  };

  // Extract branding (logo and colors) from a company website
  const extractFromWebsite = async () => {
    if (!websiteUrl.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Enter Website URL',
        text: 'Please enter the company website URL first',
        background: darkMode ? '#1F2937' : '#fff',
        color: darkMode ? '#F9FAFB' : '#111827'
      });
      return;
    }

    // Normalize URL
    let url = websiteUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    setExtractingFromWebsite(true);

    try {
      const { data } = await api.post('/api/white-label/extract-from-website', { url });

      if (data.success) {
        const updates = {};
        const extracted = [];

        // Apply logo if found
        if (data.logoUrl) {
          updates.logoUrl = data.logoUrl;
          extracted.push('logo');
        }

        // Apply favicon if found
        if (data.faviconUrl) {
          updates.faviconUrl = data.faviconUrl;
          extracted.push('favicon');
        }

        // Apply colors if found from CSS/HTML
        if (data.colors && data.colors.length > 0) {
          const primaryColor = data.colors[0];
          const scheme = ColorUtils.generateScheme(primaryColor);
          if (scheme) {
            updates.primaryColor = scheme.primary;
            updates.secondaryColor = scheme.secondary;
            updates.accentColor = scheme.accent;
            setExtractedColors(data.colors.map(c => ({ hex: c, percent: 100 / data.colors.length })));
            setSuggestedScheme(scheme);
          }
          extracted.push('colors');
        } else if (data.logoUrl) {
          // No colors found in CSS - extract colors from logo image
          try {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
              try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const colors = ColorUtils.extractColorsFromImage(imageData, 6);

                if (colors.length > 0) {
                  console.log('[WhiteLabelAdmin] Extracted colors from logo:', colors);
                  const scheme = {
                    primary: colors[0].hex,
                    secondary: colors.length > 1 ? colors[1].hex : ColorUtils.darken(colors[0].hex, 15),
                    accent: colors.length > 2 ? colors[2].hex : ColorUtils.lighten(colors[0].hex, 10)
                  };

                  setConfig(prev => ({
                    ...prev,
                    primaryColor: scheme.primary,
                    secondaryColor: scheme.secondary,
                    accentColor: scheme.accent
                  }));
                  setExtractedColors(colors.map(c => ({ hex: c.hex, percent: (c.count / colors.reduce((a, b) => a + b.count, 0)) * 100 })));
                  setSuggestedScheme(scheme);

                  Swal.fire({
                    icon: 'info',
                    title: 'Colors Extracted from Logo',
                    html: 'No brand colors found in website CSS, but colors were extracted from the logo.<br/><br/>Check the <strong>Suggested Scheme</strong> in the Color Scheme section.',
                    background: darkMode ? '#1F2937' : '#fff',
                    color: darkMode ? '#F9FAFB' : '#111827'
                  });
                }
              } catch (e) {
                console.error('[WhiteLabelAdmin] Error extracting colors from logo:', e);
              }
            };
            img.onerror = () => {
              console.warn('[WhiteLabelAdmin] Could not load logo for color extraction (CORS)');
            };
            img.src = data.logoUrl;
          } catch (e) {
            console.error('[WhiteLabelAdmin] Error setting up logo color extraction:', e);
          }
        }

        // Apply company name if found
        if (data.companyName) {
          updates.companyName = data.companyName;
          extracted.push('company name');
        }

        // Apply tagline/description if found
        if (data.tagline) {
          updates.tagline = data.tagline;
          extracted.push('tagline');
        }

        // Update domain
        try {
          const urlObj = new URL(url);
          updates.domain = urlObj.hostname;
        } catch (e) {
          // Ignore URL parsing errors
        }

        if (Object.keys(updates).length > 0) {
          setConfig(prev => ({ ...prev, ...updates }));

          Swal.fire({
            icon: 'success',
            title: 'Branding Extracted',
            html: `Successfully extracted: <strong>${extracted.join(', ')}</strong>`,
            background: darkMode ? '#1F2937' : '#fff',
            color: darkMode ? '#F9FAFB' : '#111827'
          });
        } else {
          Swal.fire({
            icon: 'info',
            title: 'No Branding Found',
            text: 'Could not extract branding from this website. Try uploading a logo manually.',
            background: darkMode ? '#1F2937' : '#fff',
            color: darkMode ? '#F9FAFB' : '#111827'
          });
        }
      }
    } catch (error) {
      console.error('Error extracting from website:', error);
      Swal.fire({
        icon: 'error',
        title: 'Extraction Failed',
        text: error.response?.data?.message || 'Could not extract branding from this website',
        background: darkMode ? '#1F2937' : '#fff',
        color: darkMode ? '#F9FAFB' : '#111827'
      });
    } finally {
      setExtractingFromWebsite(false);
    }
  };

  const saveConfiguration = async () => {
    setSaving(true);
    try {
      // Use tenantId from config, or fall back to selectedProfile.id
      const tenantId = config.tenantId || selectedProfile?.id;
      // console.log('[WhiteLabelAdmin] Saving config, tenantId:', tenantId);
      const payload = {
        company_name: config.companyName,
        tagline: config.tagline,
        custom_domain: config.domain,
        primary_color: config.primaryColor,
        secondary_color: config.secondaryColor,
        accent_color: config.accentColor,
        logo_url: config.logoUrl,
        logo_dark_url: config.logoDarkUrl,
        favicon_url: config.faviconUrl,
        email_logo_url: config.emailLogoUrl,
        login_bg_image_url: config.loginBgImage,
        powered_by: config.poweredBy,
        support_email: config.supportEmail,
        use_custom_emails: config.useCustomEmails,
        // Letterhead / PDF branding fields
        letterhead_logo_url: config.letterheadLogoUrl,
        letterhead_company_name: config.letterheadCompanyName,
        letterhead_tagline: config.letterheadTagline,
        letterhead_registration_number: config.letterheadRegistrationNumber,
        letterhead_signatory_name: config.letterheadSignatoryName,
        letterhead_signatory_title: config.letterheadSignatoryTitle,
        letterhead_address: config.letterheadAddress,
        letterhead_city: config.letterheadCity,
        letterhead_country: config.letterheadCountry,
        letterhead_phone: config.letterheadPhone,
        letterhead_email: config.letterheadEmail,
        letterhead_website: config.letterheadWebsite,
        letterhead_header_gradient_start: config.letterheadHeaderGradientStart,
        letterhead_header_gradient_end: config.letterheadHeaderGradientEnd,
        letterhead_footer_color: config.letterheadFooterColor
      };

      if (tenantId) {
        await api.put(`/api/white-label/tenants/${tenantId}`, payload);
      } else {
        payload.tenant_code = 'halisoft';
        await api.post('/api/white-label/tenants', payload);
      }

      // Apply colors to CSS variables immediately
      document.documentElement.style.setProperty('--color-primary', config.primaryColor);
      document.documentElement.style.setProperty('--color-secondary', config.secondaryColor);
      document.documentElement.style.setProperty('--color-accent', config.accentColor);

      // Refresh profiles list to show updated data
      await fetchProfiles();

      // Update selected profile with new colors
      if (selectedProfile) {
        setSelectedProfile({
          ...selectedProfile,
          primaryColor: config.primaryColor,
          secondaryColor: config.secondaryColor,
          accentColor: config.accentColor
        });
      }

      // IMPORTANT: Refresh the app-wide white-label context to update the top bar
      // Use the tenant code from the currently edited profile or the current profile code
      const tenantCodeToRefresh = config.tenantCode || selectedProfile?.tenantCode || currentProfileCode;
      if (tenantCodeToRefresh && switchProfile) {
        console.log('[WhiteLabelAdmin] Refreshing app branding for tenant:', tenantCodeToRefresh);
        await switchProfile(tenantCodeToRefresh);
      }

      Swal.fire({
        icon: 'success',
        title: selectedProfile?.id ? 'Profile Updated' : 'Configuration Saved',
        text: selectedProfile?.id
          ? `"${config.companyName}" profile has been updated successfully.`
          : 'Your branding settings have been saved.',
        timer: 2500,
        showConfirmButton: false,
        background: darkMode ? '#1F2937' : '#fff',
        color: darkMode ? '#F9FAFB' : '#111827'
      });

      // Call the onSaveSuccess callback if provided (for embedded mode)
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error('Error saving config:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save configuration',
        background: darkMode ? '#1F2937' : '#fff',
        color: darkMode ? '#F9FAFB' : '#111827'
      });
    } finally {
      setSaving(false);
    }
  };

  // Reset to default configuration
  const resetToDefaults = async () => {
    const result = await Swal.fire({
      title: 'Reset to Defaults?',
      text: 'This will reset all branding settings to HaliTrade defaults. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, reset it!',
      cancelButtonText: 'Cancel',
      background: darkMode ? '#1F2937' : '#fff',
      color: darkMode ? '#F9FAFB' : '#111827'
    });

    if (result.isConfirmed) {
      setResetting(true);
      try {
        // Reset local state to defaults (keeping tenantId for update)
        const newConfig = { ...DEFAULT_CONFIG, tenantId: config.tenantId };
        setConfig(newConfig);

        // Save defaults to server if we have a tenant
        if (config.tenantId) {
          const payload = {
            company_name: DEFAULT_CONFIG.companyName,
            tagline: DEFAULT_CONFIG.tagline,
            custom_domain: DEFAULT_CONFIG.domain,
            primary_color: DEFAULT_CONFIG.primaryColor,
            secondary_color: DEFAULT_CONFIG.secondaryColor,
            accent_color: DEFAULT_CONFIG.accentColor,
            logo_url: DEFAULT_CONFIG.logoUrl,
            logo_dark_url: DEFAULT_CONFIG.logoDarkUrl,
            favicon_url: DEFAULT_CONFIG.faviconUrl,
            email_logo_url: DEFAULT_CONFIG.emailLogoUrl,
            login_bg_image_url: DEFAULT_CONFIG.loginBgImage,
            powered_by: DEFAULT_CONFIG.poweredBy,
            support_email: DEFAULT_CONFIG.supportEmail,
            use_custom_emails: DEFAULT_CONFIG.useCustomEmails
          };

          await api.put(`/api/white-label/tenants/${config.tenantId}`, payload);
        }

        // Reset CSS variables to defaults
        document.documentElement.style.setProperty('--color-primary', DEFAULT_CONFIG.primaryColor);
        document.documentElement.style.setProperty('--color-secondary', DEFAULT_CONFIG.secondaryColor);
        document.documentElement.style.setProperty('--color-accent', DEFAULT_CONFIG.accentColor);

        Swal.fire({
          icon: 'success',
          title: 'Reset Complete',
          text: 'Branding has been reset to HaliTrade defaults.',
          timer: 2000,
          showConfirmButton: false,
          background: darkMode ? '#1F2937' : '#fff',
          color: darkMode ? '#F9FAFB' : '#111827'
        });
      } catch (error) {
        console.error('Error resetting config:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to reset configuration',
          background: darkMode ? '#1F2937' : '#fff',
          color: darkMode ? '#F9FAFB' : '#111827'
        });
      } finally {
        setResetting(false);
      }
    }
  };

  // CSS classes based on dark mode
  const cardClass = darkMode
    ? 'bg-gray-800 border-gray-700'
    : 'bg-white border-gray-200';
  const textClass = darkMode ? 'text-white' : 'text-gray-900';
  const textMutedClass = darkMode ? 'text-gray-400' : 'text-gray-600';
  const inputClass = darkMode
    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500';
  const iconBgClass = (color) => darkMode ? `bg-${color}-900/50` : `bg-${color}-50`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className={`w-8 h-8 animate-spin ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
      </div>
    );
  }

  // Embedded mode (for Platform Settings tab)
  if (embedded) {
    return (
      <div className="space-y-6">
        {/* Header with actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className={`text-lg font-semibold ${textClass} flex items-center gap-2`}>
              <Palette className={darkMode ? 'text-orange-400' : 'text-orange-600'} size={24} />
              White Label Configuration
            </h3>
            <p className={`text-sm ${textMutedClass}`}>
              Customize your platform branding - logo, colors, and domain
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setPreview(!preview)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {preview ? <EyeOff size={18} /> : <Eye size={18} />}
              {preview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <button
              onClick={resetToDefaults}
              disabled={resetting}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                darkMode
                  ? 'bg-red-900/50 hover:bg-red-900/70 text-red-300'
                  : 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
              }`}
            >
              {resetting ? <RefreshCw size={18} className="animate-spin" /> : <RotateCcw size={18} />}
              {resetting ? 'Resetting...' : 'Reset'}
            </button>
            <button
              onClick={saveConfiguration}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Profile Selector */}
        <div className={`p-4 rounded-xl border ${cardClass}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${darkMode ? 'bg-indigo-900/50' : 'bg-indigo-50'}`}>
                <Copy className={darkMode ? 'text-indigo-400' : 'text-indigo-600'} size={20} />
              </div>
              <div>
                <h4 className={`font-semibold ${textClass}`}>Brand Profiles</h4>
                <p className={`text-xs ${textMutedClass}`}>
                  {selectedProfile ? (
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: config.primaryColor }}
                      />
                      Editing: <strong>{selectedProfile.name}</strong>
                    </span>
                  ) : 'Select or create a profile'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600'
                      : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
                  }`}
                >
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: selectedProfile?.primaryColor || config.primaryColor }}
                  />
                  <span>{selectedProfile?.name || 'Select Profile'}</span>
                  <ChevronDown size={16} className={`transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showProfileDropdown && (
                  <div className={`absolute z-50 mt-2 w-72 rounded-xl shadow-xl border ${
                    darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                    {/* Preset Profiles */}
                    <div className={`px-3 py-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <p className={`text-xs font-semibold uppercase ${textMutedClass}`}>Presets</p>
                    </div>
                    <div className="py-1">
                      {Object.entries(PRESET_PROFILES).map(([key, profile]) => (
                        <button
                          key={key}
                          onClick={() => loadProfile(profile)}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors ${
                            darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                          } ${selectedProfile?.name === profile.name ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
                        >
                          <div
                            className="w-4 h-4 rounded-full border-2"
                            style={{ backgroundColor: profile.primaryColor, borderColor: profile.secondaryColor }}
                          />
                          <div className="flex-1">
                            <div className={`font-medium ${textClass}`}>{profile.name}</div>
                            <div className={`text-xs ${textMutedClass}`}>{profile.tagline}</div>
                          </div>
                          {selectedProfile?.name === profile.name && (
                            <span className="text-green-500 text-xs">Active</span>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Saved Profiles */}
                    {profiles.length > 0 && (
                      <>
                        <div className={`px-3 py-2 border-t border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <p className={`text-xs font-semibold uppercase ${textMutedClass}`}>Saved Profiles</p>
                        </div>
                        <div className="py-1 max-h-48 overflow-y-auto">
                          {profiles.map((profile) => (
                            <div
                              key={profile.id}
                              className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                              } ${selectedProfile?.id === profile.id ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
                            >
                              <button
                                onClick={() => loadProfile(profile)}
                                className="flex-1 flex items-center gap-3 text-left"
                              >
                                <div
                                  className="w-4 h-4 rounded-full border-2"
                                  style={{ backgroundColor: profile.primaryColor, borderColor: profile.secondaryColor }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className={`font-medium truncate ${textClass}`}>
                                    {profile.name}
                                    {profile.isDefault && (
                                      <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-500/20 text-green-400 rounded">
                                        Default
                                      </span>
                                    )}
                                  </div>
                                  <div className={`text-xs truncate ${textMutedClass}`}>{profile.domain}</div>
                                </div>
                                {selectedProfile?.id === profile.id && (
                                  <span className="text-green-500 text-xs">Active</span>
                                )}
                              </button>
                              {!profile.isDefault && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteProfile(profile);
                                  }}
                                  className={`p-1 rounded hover:bg-red-500/20 transition-colors ${
                                    darkMode ? 'text-red-400' : 'text-red-600'
                                  }`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Close dropdown on outside click */}
                    <div className={`px-3 py-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <button
                        onClick={() => setShowProfileDropdown(false)}
                        className={`w-full text-center text-xs ${textMutedClass} hover:underline`}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Create New Profile Button */}
              <button
                onClick={saveAsNewProfile}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  darkMode
                    ? 'bg-green-900/50 hover:bg-green-900/70 text-green-300'
                    : 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-200'
                }`}
              >
                <Plus size={16} />
                New Profile
              </button>

              {/* Apply Changes to Profile Button */}
              {selectedProfile?.id && (
                <button
                  onClick={saveConfiguration}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                  {saving ? 'Saving...' : 'Save and Apply'}
                </button>
              )}
            </div>
          </div>

          {/* Current Profile Info */}
          {selectedProfile && (
            <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className={textMutedClass}>Primary:</span>
                  <div
                    className="w-6 h-6 rounded border shadow-sm"
                    style={{ backgroundColor: config.primaryColor }}
                  />
                  <code className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    {config.primaryColor}
                  </code>
                </div>
                <div className="flex items-center gap-2">
                  <span className={textMutedClass}>Secondary:</span>
                  <div
                    className="w-6 h-6 rounded border shadow-sm"
                    style={{ backgroundColor: config.secondaryColor }}
                  />
                  <code className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    {config.secondaryColor}
                  </code>
                </div>
                <div className="flex items-center gap-2">
                  <span className={textMutedClass}>Accent:</span>
                  <div
                    className="w-6 h-6 rounded border shadow-sm"
                    style={{ backgroundColor: config.accentColor }}
                  />
                  <code className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    {config.accentColor}
                  </code>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={`grid ${preview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6`}>
          {/* Configuration Panel */}
          <div className="space-y-6">
            {/* Brand Information */}
            <div className={`p-5 rounded-xl border ${cardClass}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/50' : 'bg-blue-50'}`}>
                  <Settings className={darkMode ? 'text-blue-400' : 'text-blue-600'} size={20} />
                </div>
                <h4 className={`font-semibold ${textClass}`}>Brand Information</h4>
              </div>

              {/* Website URL extraction */}
              <div className={`mb-4 p-3 rounded-lg border ${darkMode ? 'bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-cyan-700/50' : 'bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Globe className={darkMode ? 'text-cyan-400' : 'text-cyan-600'} size={16} />
                  <span className={`text-xs font-semibold ${darkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                    Auto-extract from website
                  </span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                    placeholder="https://company-website.com"
                  />
                  <button
                    onClick={extractFromWebsite}
                    disabled={extractingFromWebsite}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 ${
                      darkMode
                        ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
                        : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                    }`}
                  >
                    {extractingFromWebsite ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <Wand2 size={16} />
                    )}
                    {extractingFromWebsite ? 'Extracting...' : 'Extract'}
                  </button>
                </div>
                <p className={`text-[10px] mt-1.5 ${darkMode ? 'text-cyan-400/70' : 'text-cyan-600/70'}`}>
                  Extracts logo, colors, and company info from the website
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Company Name</label>
                  <input
                    type="text"
                    value={config.companyName}
                    onChange={(e) => setConfig({ ...config, companyName: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${inputClass}`}
                    placeholder="HaliTrade"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Tagline</label>
                  <input
                    type="text"
                    value={config.tagline}
                    onChange={(e) => setConfig({ ...config, tagline: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${inputClass}`}
                    placeholder="Trade Finance Platform"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Domain</label>
                  <input
                    type="text"
                    value={config.domain}
                    onChange={(e) => setConfig({ ...config, domain: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${inputClass}`}
                    placeholder="app.yourcompany.com"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Powered By</label>
                  <input
                    type="text"
                    value={config.poweredBy}
                    onChange={(e) => setConfig({ ...config, poweredBy: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${inputClass}`}
                    placeholder="powered by HaliSoft"
                  />
                </div>
              </div>
            </div>

            {/* Color Scheme */}
            <div className={`p-5 rounded-xl border ${cardClass}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-purple-50'}`}>
                    <Palette className={darkMode ? 'text-purple-400' : 'text-purple-600'} size={20} />
                  </div>
                  <h4 className={`font-semibold ${textClass}`}>Color Scheme</h4>
                </div>
                <button
                  onClick={generateSchemeFromPrimary}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    darkMode
                      ? 'bg-purple-900/50 hover:bg-purple-900/70 text-purple-300'
                      : 'bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200'
                  }`}
                >
                  <Wand2 size={14} />
                  Auto-suggest
                </button>
              </div>

              {/* Hidden canvas for color extraction */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />

              {/* Extracted Colors from Logo */}
              {extractedColors.length > 0 && (
                <div className={`mb-4 p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} className={darkMode ? 'text-yellow-400' : 'text-yellow-600'} />
                      <span className={`text-xs font-semibold uppercase tracking-wide ${textMutedClass}`}>
                        Colors from Logo
                      </span>
                    </div>
                    {extractingColors && (
                      <RefreshCw size={14} className="animate-spin text-purple-400" />
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {extractedColors.map((color, index) => (
                      <button
                        key={index}
                        onClick={() => applyExtractedColor(color.hex)}
                        className="group relative"
                        title={`Click to apply ${color.hex} as primary color`}
                      >
                        <div
                          className="w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 hover:shadow-lg"
                          style={{
                            backgroundColor: color.hex,
                            borderColor: ColorUtils.isDark(color.hex) ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)'
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Check size={16} className={ColorUtils.isDark(color.hex) ? 'text-white' : 'text-gray-800'} />
                        </div>
                        <span className={`absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] font-mono ${textMutedClass} whitespace-nowrap`}>
                          {color.hex}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className={`mt-6 text-xs ${textMutedClass}`}>
                    Click a color to apply it as primary and auto-generate secondary/accent
                  </p>
                </div>
              )}

              {/* Suggested Scheme */}
              {suggestedScheme && (
                <div className={`mb-4 p-3 rounded-lg border-2 ${darkMode ? 'bg-green-900/20 border-green-500/30' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                      Suggested Scheme
                    </span>
                    <button
                      onClick={() => applyColorScheme(suggestedScheme)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        darkMode
                          ? 'bg-green-900/50 hover:bg-green-900/70 text-green-300'
                          : 'bg-green-100 hover:bg-green-200 text-green-700'
                      }`}
                    >
                      <Check size={12} />
                      Apply All
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className="w-12 h-12 rounded-lg shadow-md"
                        style={{ backgroundColor: suggestedScheme.primary }}
                      />
                      <span className={`text-[10px] mt-1 ${textMutedClass}`}>Primary</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div
                        className="w-12 h-12 rounded-lg shadow-md"
                        style={{ backgroundColor: suggestedScheme.secondary }}
                      />
                      <span className={`text-[10px] mt-1 ${textMutedClass}`}>Secondary</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div
                        className="w-12 h-12 rounded-lg shadow-md"
                        style={{ backgroundColor: suggestedScheme.accent }}
                      />
                      <span className={`text-[10px] mt-1 ${textMutedClass}`}>Accent</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {[
                  { field: 'primaryColor', label: 'Primary Color' },
                  { field: 'secondaryColor', label: 'Secondary Color' },
                  { field: 'accentColor', label: 'Accent Color' }
                ].map(({ field, label }) => (
                  <div key={field}>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>{label}</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={config[field]}
                        onChange={(e) => handleColorChange(field, e.target.value)}
                        className="w-12 h-10 rounded-lg cursor-pointer border-2 border-gray-300"
                      />
                      <input
                        type="text"
                        value={config[field]}
                        onChange={(e) => handleColorChange(field, e.target.value)}
                        className={`flex-1 px-3 py-2 border rounded-lg font-mono text-sm ${inputClass}`}
                      />
                      <div
                        className="w-10 h-10 rounded-lg border-2 border-gray-200 shadow-sm"
                        style={{ backgroundColor: config[field] }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Tip about logo color extraction */}
              {!config.logoUrl && extractedColors.length === 0 && (
                <div className={`mt-4 p-3 rounded-lg text-xs ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                  <strong>Tip:</strong> Upload a logo below to automatically extract colors from it
                </div>
              )}
            </div>

            {/* Logos & Images */}
            <div className={`p-5 rounded-xl border ${cardClass}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/50' : 'bg-green-50'}`}>
                  <ImageIcon className={darkMode ? 'text-green-400' : 'text-green-600'} size={20} />
                </div>
                <h4 className={`font-semibold ${textClass}`}>Logos & Images</h4>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { field: 'logoUrl', label: 'Primary Logo', hint: 'PNG transparent' },
                  { field: 'logoDarkUrl', label: 'Dark Logo', hint: 'Light colored' },
                  { field: 'faviconUrl', label: 'Favicon', hint: '32x32' },
                  { field: 'emailLogoUrl', label: 'Email Logo', hint: 'For emails' }
                ].map(({ field, label, hint }) => (
                  <div key={field}>
                    <label className={`block text-xs font-medium mb-1 ${textMutedClass}`}>{label}</label>
                    <div
                      className={`p-2 rounded-lg border-2 border-dashed transition-all duration-200 ${
                        dragActive[field]
                          ? darkMode
                            ? 'border-green-400 bg-green-900/30'
                            : 'border-green-500 bg-green-50'
                          : darkMode
                            ? 'border-gray-600 hover:border-gray-500'
                            : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDragEnter={(e) => handleDrag(field, e)}
                      onDragLeave={(e) => handleDrag(field, e)}
                      onDragOver={(e) => handleDrag(field, e)}
                      onDrop={(e) => handleDrop(field, e)}
                    >
                      {config[field] ? (
                        <div className="flex flex-col items-center gap-1.5">
                          <img src={config[field]} alt={label} className="h-8 max-w-[80px] object-contain rounded" />
                          <div className="flex gap-1">
                            <label className={`flex items-center gap-1 px-1.5 py-1 rounded cursor-pointer text-[10px] font-medium transition-colors ${
                              darkMode ? 'bg-green-900/50 text-green-300 hover:bg-green-900/70' : 'bg-green-50 text-green-700 hover:bg-green-100'
                            }`}>
                              <Upload size={10} />
                              <input type="file" accept="image/*" onChange={(e) => handleFileUpload(field, e)} className="hidden" />
                            </label>
                            <button
                              onClick={() => setConfig(prev => ({ ...prev, [field]: null }))}
                              className={`flex items-center gap-1 px-1.5 py-1 rounded text-[10px] font-medium transition-colors ${
                                darkMode ? 'bg-red-900/50 text-red-300 hover:bg-red-900/70' : 'bg-red-50 text-red-700 hover:bg-red-100'
                              }`}
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1 py-1">
                          <div className={`p-1.5 rounded-full ${darkMode ? 'bg-green-900/50' : 'bg-green-50'}`}>
                            <Upload size={12} className={darkMode ? 'text-green-400' : 'text-green-600'} />
                          </div>
                          <p className={`text-[10px] ${textMutedClass} text-center`}>
                            {dragActive[field] ? 'Drop here' : hint}
                          </p>
                          <label className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer text-[10px] font-medium transition-colors ${
                            darkMode ? 'bg-green-900/50 text-green-300 hover:bg-green-900/70' : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}>
                            <Upload size={10} />
                            Browse
                            <input type="file" accept="image/*" onChange={(e) => handleFileUpload(field, e)} className="hidden" />
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Letterhead / PDF Branding Section */}
            <div className={`p-5 rounded-xl border ${cardClass}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${darkMode ? 'bg-cyan-900/50' : 'bg-cyan-50'}`}>
                  <FileText className={darkMode ? 'text-cyan-400' : 'text-cyan-600'} size={20} />
                </div>
                <div>
                  <h4 className={`font-semibold ${textClass}`}>Letterhead & PDF Branding</h4>
                  <p className={`text-xs ${textMutedClass}`}>Configure headers and footers for generated PDFs and documents</p>
                </div>
              </div>

              {/* Header Configuration */}
              <div className={`p-4 rounded-lg mb-4 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h5 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textClass}`}>
                  <Building2 size={16} />
                  Header Configuration
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Company Name (for letterhead)</label>
                    <input
                      type="text"
                      value={config.letterheadCompanyName || config.companyName}
                      onChange={(e) => setConfig({ ...config, letterheadCompanyName: e.target.value })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                      placeholder="HaliSoft L.L.C-FZ"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Header Tagline</label>
                    <input
                      type="text"
                      value={config.letterheadTagline || config.tagline}
                      onChange={(e) => setConfig({ ...config, letterheadTagline: e.target.value })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                      placeholder="Empowering Trade Finance with AI"
                    />
                  </div>
                </div>

                {/* Header Gradient Colors */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Header Gradient Start</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={config.letterheadHeaderGradientStart}
                        onChange={(e) => setConfig({ ...config, letterheadHeaderGradientStart: e.target.value })}
                        className="w-12 h-10 rounded-lg cursor-pointer border-2 border-gray-300"
                      />
                      <input
                        type="text"
                        value={config.letterheadHeaderGradientStart}
                        onChange={(e) => setConfig({ ...config, letterheadHeaderGradientStart: e.target.value })}
                        className={`flex-1 px-3 py-2 border rounded-lg font-mono text-sm ${inputClass}`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Header Gradient End</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={config.letterheadHeaderGradientEnd}
                        onChange={(e) => setConfig({ ...config, letterheadHeaderGradientEnd: e.target.value })}
                        className="w-12 h-10 rounded-lg cursor-pointer border-2 border-gray-300"
                      />
                      <input
                        type="text"
                        value={config.letterheadHeaderGradientEnd}
                        onChange={(e) => setConfig({ ...config, letterheadHeaderGradientEnd: e.target.value })}
                        className={`flex-1 px-3 py-2 border rounded-lg font-mono text-sm ${inputClass}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Letterhead Logo Upload with Drag & Drop */}
                <div className="mt-4">
                  <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Letterhead Logo (for PDFs)</label>
                  <div
                    className={`p-3 rounded-lg border-2 border-dashed transition-all duration-200 ${
                      dragActive.letterheadLogoUrl
                        ? darkMode
                          ? 'border-cyan-400 bg-cyan-900/30'
                          : 'border-cyan-500 bg-cyan-50'
                        : darkMode
                          ? 'border-gray-600 hover:border-gray-500'
                          : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onDragEnter={(e) => handleDrag('letterheadLogoUrl', e)}
                    onDragLeave={(e) => handleDrag('letterheadLogoUrl', e)}
                    onDragOver={(e) => handleDrag('letterheadLogoUrl', e)}
                    onDrop={(e) => handleDrop('letterheadLogoUrl', e)}
                  >
                    {config.letterheadLogoUrl ? (
                      <div className="flex items-center gap-3">
                        <img src={config.letterheadLogoUrl} alt="Letterhead Logo" className="h-10 max-w-[120px] object-contain rounded" />
                        <label className={`flex items-center gap-1.5 px-2 py-1.5 rounded cursor-pointer text-xs font-medium transition-colors ${
                          darkMode ? 'bg-cyan-900/50 text-cyan-300 hover:bg-cyan-900/70' : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
                        }`}>
                          <Upload size={12} />
                          Change
                          <input type="file" accept="image/*" onChange={(e) => handleFileUpload('letterheadLogoUrl', e)} className="hidden" />
                        </label>
                        <button
                          onClick={() => setConfig(prev => ({ ...prev, letterheadLogoUrl: null }))}
                          className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                            darkMode ? 'bg-red-900/50 text-red-300 hover:bg-red-900/70' : 'bg-red-50 text-red-700 hover:bg-red-100'
                          }`}
                        >
                          <Trash2 size={12} />
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 py-2">
                        <div className={`p-2 rounded-full ${darkMode ? 'bg-cyan-900/50' : 'bg-cyan-50'}`}>
                          <Upload size={16} className={darkMode ? 'text-cyan-400' : 'text-cyan-600'} />
                        </div>
                        <div className="flex-1">
                          <p className={`text-xs font-medium ${textClass}`}>
                            {dragActive.letterheadLogoUrl ? 'Drop logo here' : 'Drag & drop logo'}
                          </p>
                          <p className={`text-[10px] ${textMutedClass}`}>PNG with transparent bg</p>
                        </div>
                        <label className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded cursor-pointer text-xs font-medium transition-colors ${
                          darkMode ? 'bg-cyan-900/50 text-cyan-300 hover:bg-cyan-900/70' : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
                        }`}>
                          <Upload size={12} />
                          Browse
                          <input type="file" accept="image/*" onChange={(e) => handleFileUpload('letterheadLogoUrl', e)} className="hidden" />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Configuration */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h5 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textClass}`}>
                  <MapPin size={16} />
                  Footer Configuration
                </h5>
                {/* Signatory Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Signatory Name</label>
                    <input
                      type="text"
                      value={config.letterheadSignatoryName}
                      onChange={(e) => setConfig({ ...config, letterheadSignatoryName: e.target.value })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                      placeholder="Loic Leray"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Signatory Title</label>
                    <input
                      type="text"
                      value={config.letterheadSignatoryTitle}
                      onChange={(e) => setConfig({ ...config, letterheadSignatoryTitle: e.target.value })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                      placeholder="Founder & CEO"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Registration Number</label>
                    <input
                      type="text"
                      value={config.letterheadRegistrationNumber}
                      onChange={(e) => setConfig({ ...config, letterheadRegistrationNumber: e.target.value })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                      placeholder="UAE-FZ-12345678"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Address</label>
                    <input
                      type="text"
                      value={config.letterheadAddress}
                      onChange={(e) => setConfig({ ...config, letterheadAddress: e.target.value })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                      placeholder="Business Bay"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>City</label>
                    <input
                      type="text"
                      value={config.letterheadCity}
                      onChange={(e) => setConfig({ ...config, letterheadCity: e.target.value })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                      placeholder="Dubai"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Country</label>
                    <input
                      type="text"
                      value={config.letterheadCountry}
                      onChange={(e) => setConfig({ ...config, letterheadCountry: e.target.value })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                      placeholder="UAE"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Phone</label>
                    <div className="relative">
                      <Phone size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textMutedClass}`} />
                      <input
                        type="text"
                        value={config.letterheadPhone}
                        onChange={(e) => setConfig({ ...config, letterheadPhone: e.target.value })}
                        className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                        placeholder="+971 4 123 4567"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Email</label>
                    <div className="relative">
                      <Mail size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textMutedClass}`} />
                      <input
                        type="email"
                        value={config.letterheadEmail || config.supportEmail}
                        onChange={(e) => setConfig({ ...config, letterheadEmail: e.target.value })}
                        className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                        placeholder="info@halisoft.biz"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Website</label>
                    <div className="relative">
                      <Globe size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textMutedClass}`} />
                      <input
                        type="text"
                        value={config.letterheadWebsite || config.domain}
                        onChange={(e) => setConfig({ ...config, letterheadWebsite: e.target.value })}
                        className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${inputClass}`}
                        placeholder="www.halisoft.biz"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${textMutedClass}`}>Footer Background Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={config.letterheadFooterColor}
                        onChange={(e) => setConfig({ ...config, letterheadFooterColor: e.target.value })}
                        className="w-12 h-10 rounded-lg cursor-pointer border-2 border-gray-300"
                      />
                      <input
                        type="text"
                        value={config.letterheadFooterColor}
                        onChange={(e) => setConfig({ ...config, letterheadFooterColor: e.target.value })}
                        className={`flex-1 px-3 py-2 border rounded-lg font-mono text-sm ${inputClass}`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Copy from Main Branding Button */}
              <button
                onClick={() => setConfig({
                  ...config,
                  letterheadCompanyName: config.companyName,
                  letterheadTagline: config.tagline,
                  letterheadEmail: config.supportEmail,
                  letterheadWebsite: config.domain,
                  letterheadHeaderGradientStart: config.primaryColor,
                  letterheadHeaderGradientEnd: config.secondaryColor
                })}
                className={`mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  darkMode
                    ? 'bg-cyan-900/50 hover:bg-cyan-900/70 text-cyan-300 border border-cyan-700'
                    : 'bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200'
                }`}
              >
                <Copy size={16} />
                Copy from Main Branding Settings
              </button>
            </div>
          </div>

          {/* Preview Panel */}
          {preview && (
            <div className="lg:sticky lg:top-4 h-fit">
              <div className={`p-5 rounded-xl border ${cardClass}`}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className={`font-semibold ${textClass}`}>Live Preview</h4>
                  <div className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-semibold">
                    LIVE
                  </div>
                </div>

                {/* Preview of application with applied settings */}
                <div
                  className="border-2 rounded-xl overflow-hidden shadow-lg"
                  style={{ borderColor: config.primaryColor }}
                >
                  {/* Header Preview */}
                  <div
                    className="p-4 text-white"
                    style={{ backgroundColor: config.primaryColor }}
                  >
                    <div className="flex items-center gap-3">
                      {config.logoUrl ? (
                        <img src={config.logoUrl} alt="Logo" className="h-8 max-w-[120px] object-contain bg-white/10 rounded px-2 py-1" />
                      ) : (
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-xs font-bold">
                          LOGO
                        </div>
                      )}
                      <span className="font-bold text-lg">{config.companyName}</span>
                    </div>
                  </div>

                  {/* Body Preview */}
                  <div className="p-6 bg-gray-50">
                    <h3 className="text-xl font-bold mb-1 text-gray-900">Welcome to {config.companyName}</h3>
                    <p className="text-gray-600 mb-4 text-sm">{config.tagline}</p>

                    <button
                      className="px-4 py-2 rounded-lg text-white font-medium mb-2 w-full text-sm"
                      style={{ backgroundColor: config.primaryColor }}
                    >
                      Submit Invoice
                    </button>

                    <button
                      className="px-4 py-2 rounded-lg text-white font-medium mb-2 w-full text-sm"
                      style={{ backgroundColor: config.secondaryColor }}
                    >
                      View Transactions
                    </button>

                    <button
                      className="px-4 py-2 rounded-lg text-white font-medium w-full text-sm"
                      style={{ backgroundColor: config.accentColor }}
                    >
                      Contact Support
                    </button>

                    {/* Stats Preview */}
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      {[
                        { label: 'Volume', value: '€125K', color: config.primaryColor },
                        { label: 'Active', value: '45', color: config.secondaryColor },
                        { label: 'Rate', value: '2.5%', color: config.accentColor }
                      ].map((stat) => (
                        <div
                          key={stat.label}
                          className="p-3 bg-white rounded-lg shadow-sm"
                          style={{ borderLeftWidth: '3px', borderLeftColor: stat.color }}
                        >
                          <div className="text-xs text-gray-500">{stat.label}</div>
                          <div className="text-lg font-bold text-gray-900">{stat.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer Preview */}
                  <div
                    className="p-3 text-white text-xs text-center"
                    style={{ backgroundColor: config.secondaryColor }}
                  >
                    © 2024 {config.companyName} | {config.domain}
                    {config.poweredBy && <span className="block opacity-70">{config.poweredBy}</span>}
                  </div>
                </div>

                <div className={`mt-4 p-3 rounded-lg text-sm ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-800'}`}>
                  <strong>Tip:</strong> Changes are visible instantly. Test different color combinations to find your perfect brand identity.
                </div>
              </div>

              {/* Letterhead Preview */}
              <div className={`p-5 rounded-xl border mt-4 ${cardClass}`}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className={`font-semibold ${textClass}`}>Letterhead Preview</h4>
                  <div className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-xs font-semibold">
                    PDF/DOCUMENT
                  </div>
                </div>

                {/* Letterhead Document Preview - Professional Corporate Letter */}
                <div className="border rounded-lg overflow-hidden shadow-xl bg-white flex flex-col" style={{ minHeight: '600px' }}>
                  {/* HEADER - Logo + Company Info centered */}
                  <div className="px-6 py-3 bg-white border-b text-center">
                    {/* Logo Row */}
                    <div className="flex items-center justify-start gap-2 mb-2">
                      {config.letterheadLogoUrl ? (
                        <img src={config.letterheadLogoUrl} alt="Logo" className="h-7 object-contain" />
                      ) : (
                        <div className="flex items-center gap-1">
                          <div className="w-5 h-5 bg-red-600 rounded flex items-center justify-center text-white text-[10px] font-bold">H</div>
                          <span className="text-red-600 font-bold text-sm">Hali<span className="text-gray-700">Trade</span></span>
                        </div>
                      )}
                      <span className="text-[9px] text-gray-500">powered by HaliSoft</span>
                    </div>
                    {/* Company Name - Black, centered, uppercase */}
                    <h1 className="text-lg font-bold uppercase tracking-wide text-gray-900">
                      {config.letterheadCompanyName || config.companyName}
                    </h1>
                    {/* Tagline - Black, italic */}
                    <p className="text-xs italic text-gray-700">
                      {config.letterheadTagline || config.tagline || 'AI-Powered Trade Finance Solutions'}
                    </p>
                    {/* Address line */}
                    <p className="text-[9px] text-gray-600 mt-1">
                      {config.letterheadAddress}{config.letterheadAddress && (config.letterheadCity || config.letterheadCountry) ? ' | ' : ''}
                      {[config.letterheadCity, config.letterheadCountry].filter(Boolean).join(', ')}
                    </p>
                    {/* Contact line */}
                    <p className="text-[9px] text-gray-600">
                      {[
                        config.letterheadPhone && `Tel: ${config.letterheadPhone}`,
                        config.letterheadEmail && `Email: ${config.letterheadEmail}`,
                        config.letterheadWebsite
                      ].filter(Boolean).join(' | ')}
                    </p>
                  </div>

                  {/* LETTER BODY */}
                  <div className="px-6 py-4 flex-1 bg-white text-sm">
                    {/* Date - Right aligned */}
                    <div className="text-right text-gray-700 mb-4">
                      {config.letterheadCity ? `${config.letterheadCity}, ` : ''}
                      {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>

                    {/* Recipient */}
                    <div className="mb-4 text-gray-800 text-xs leading-relaxed">
                      <div>Mr. Jean Dupont</div>
                      <div>Chief Executive Officer</div>
                      <div>ABC Trading Company</div>
                      <div>123 Business Avenue</div>
                      <div>Paris, France</div>
                    </div>

                    {/* Subject - Bold */}
                    <div className="mb-3">
                      <span className="font-bold text-gray-800">Re: Invoice Factoring Services Proposal</span>
                    </div>

                    {/* Salutation */}
                    <div className="mb-3 text-gray-800 text-xs">Dear Mr. Dupont,</div>

                    {/* Content */}
                    <div className="text-xs text-gray-600 space-y-2 leading-relaxed">
                      <p>We are pleased to introduce HaliTrade, powered by HaliSoft, your innovative partner in invoice financing. Our revolutionary technology platform enables your business to transform receivables into immediate liquidity.</p>
                      <p>Through our AI-powered system, we reduce approval times from 3-5 weeks to just 24-72 hours.</p>
                    </div>

                    {/* Closing */}
                    <div className="mt-4 text-xs text-gray-800">
                      <div className="mb-3">Yours sincerely,</div>
                      <div className="mt-4">{config.letterheadSignatoryName || 'Loic Leray'}</div>
                      <div className="font-bold">{config.letterheadSignatoryTitle || 'Founder & CEO'}</div>
                      <div>{config.letterheadCompanyName || config.companyName}</div>
                    </div>
                  </div>

                  {/* FOOTER - 5 lines like PDF - all black/gray except Confidential in red */}
                  <div className="px-6 py-2 bg-white border-t text-center text-[8px] leading-relaxed text-gray-800">
                    {/* Line 1: Company | Address */}
                    <div>
                      {config.letterheadCompanyName || config.companyName}
                      {' | '}
                      {[config.letterheadAddress || 'IFZA Business Park', config.letterheadCity || 'Dubai', config.letterheadCountry || 'UAE'].join(', ')}
                    </div>
                    {/* Line 2: Tel | Email | Web */}
                    <div>
                      Tel: {config.letterheadPhone || '+971 XX XXX XXXX'}
                      {' | '}
                      Email: {config.letterheadEmail || 'contact@halisoft.com'}
                      {' | '}
                      Web: {config.letterheadWebsite || 'www.halisoft.com'}
                    </div>
                    {/* Line 3: Trade License | Tax Registration - italic */}
                    <div className="italic">
                      Trade License: {config.letterheadRegistrationNumber || 'XXXXX'} | Tax Registration: XXXXX
                    </div>
                    {/* Line 4: Page number */}
                    <div>Page 1 of 1</div>
                    {/* Line 5: Confidential - red, italic */}
                    <div className="text-red-600 italic">Confidential - This document contains proprietary information</div>
                  </div>
                </div>

                <div className={`mt-4 p-3 rounded-lg text-sm ${darkMode ? 'bg-cyan-900/30 text-cyan-300' : 'bg-cyan-50 text-cyan-800'}`}>
                  <strong>Document Preview:</strong> This is how your generated PDFs and official documents will appear with the letterhead branding.
                </div>

                {/* Download Test PDF Button */}
                <div className="mt-4">
                  <button
                    onClick={async () => {
                      try {
                        await generateSamplePDF(config);
                        Swal.fire({
                          icon: 'success',
                          title: 'PDF Generated',
                          text: 'Sample PDF has been downloaded successfully!',
                          timer: 2000,
                          showConfirmButton: false
                        });
                      } catch (error) {
                        console.error('Error generating PDF:', error);
                        Swal.fire({
                          icon: 'error',
                          title: 'Error',
                          text: 'Failed to generate PDF. Please check the console for details.'
                        });
                      }
                    }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                      darkMode
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                  >
                    <Download size={18} />
                    Download Test PDF
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Standalone mode (full page)
  return (
    <div className={`min-h-screen p-4 md:p-8 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`rounded-xl shadow-sm border p-6 mb-6 ${cardClass}`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className={`text-3xl font-bold flex items-center gap-3 ${textClass}`}>
                <Palette className={darkMode ? 'text-orange-400' : 'text-orange-600'} size={32} />
                White Label Configuration
              </h1>
              <p className={`mt-1 ${textMutedClass}`}>Customize your platform's branding and appearance</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setPreview(!preview)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all shadow-sm hover:shadow ${
                  darkMode
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                {preview ? <EyeOff size={20} /> : <Eye size={20} />}
                <span className="font-medium">{preview ? 'Hide Preview' : 'Show Preview'}</span>
              </button>
              <button
                onClick={resetToDefaults}
                disabled={resetting}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all shadow-sm hover:shadow disabled:opacity-50 ${
                  darkMode
                    ? 'bg-red-900/50 hover:bg-red-900/70 text-red-300'
                    : 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
                }`}
              >
                {resetting ? <RefreshCw size={20} className="animate-spin" /> : <RotateCcw size={20} />}
                <span className="font-medium">{resetting ? 'Resetting...' : 'Reset'}</span>
              </button>
              <button
                onClick={saveConfiguration}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all shadow-sm hover:shadow disabled:opacity-50"
              >
                {saving ? <RefreshCw size={20} className="animate-spin" /> : <Save size={20} />}
                <span className="font-medium">{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Same content as embedded mode but full-width */}
        <div className={`grid ${preview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6`}>
          {/* Configuration panels... (same as embedded) */}
          {/* Copy embedded content here for standalone */}
        </div>
      </div>
    </div>
  );
};

export default WhiteLabelAdmin;
